/** @jsxImportSource @emotion/react */
import { cx } from '@emotion/css';
import { FloatingDelayGroup } from '@floating-ui/react';
import type { HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

interface Props extends HTMLAttributes<HTMLDivElement> {
  /** Horizontally align the items */
  align?: 'start' | 'center' | 'end' | 'between';
  /** Vertically align the items */
  verticalAlign?: 'start' | 'center' | 'end' | 'baseline' | 'stretch' | 'normal';
  children: ReactNode;
  /** Change the space between items */
  spacing?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'none';
  /** Display it inline with another component */
  inline?: boolean;
  /** Should the items wrap */
  wrap?: boolean;
  /** Make it a segmented control, no spacing -1px overlay */
  segmented?: boolean;
  /** Make it vertically stacked */
  stacked?: boolean;
  /** If used inside a ODSGrid, pass in the columns */
  columns?: number;
  /** Ignore */
  controlGroup?: boolean;
  /** Want the corners to be rounded */
  round?: 'bottom' | 'top' | 'none' | 'both';
  /** Useful for a radio button group name */
  name?: string;
  testSelector?: string;
  /** Want it to take up all the space */
  fullWidth?: boolean;
  /** Use when multiple items with tooltips will be in 1 group */
  delayGroup?: boolean;
  /** Set this group disabled */
  disabled?: boolean;
  /** Add a border to the group and between children */
  border?: boolean;
}

const ODSGroup = forwardRef<HTMLDivElement, Props>(function ODSGroup(
  {
    className,
    align,
    verticalAlign,
    children,
    spacing = 'md',
    inline,
    wrap,
    segmented,
    stacked,
    columns,
    controlGroup,
    round = 'both',
    name,
    testSelector,
    fullWidth,
    delayGroup,
    disabled,
    border,
    ...componentProps
  },
  ref
) {
  const display = inline ? 'd-inline-flex' : 'd-flex';
  let gap = 'gap-0';

  if (!segmented && !controlGroup) {
    switch (spacing) {
      case 'xs':
        gap = '';
        break;
      case 'sm':
        gap = 'gap-1';
        break;
      case 'md':
        gap = 'gap-2';
        break;
      case 'lg':
        gap = 'gap-3';
        break;
      case 'xl':
        gap = 'gap-4';
        break;
      default:
    }
  }

  return (
    <div
      data-testid={testSelector}
      ref={ref}
      className={cx(
        display,
        `ods-group`,
        verticalAlign && `align-items-${verticalAlign}`,
        align && `justify-content-${align}`,
        {
          'flex-wrap': wrap && !segmented,
          'flex-column': stacked,
          'ods-control-group': controlGroup,
          'w-100': (!!stacked && !inline) || fullWidth,
          disabled,
        },
        gap,
        className
      )}
      css={(theme) => ({
        '&:empty': {
          display: 'none !important',
        },
        ...(spacing === 'xs' && {
          gap: '0.125rem',
        }),
        ...(columns && {
          gridColumn: `span ${columns}`,
        }),
        ...((segmented || border) && {
          '& > :active, & > .active, & > :focus': {
            zIndex: 3,
          },
          '& > *, > .dropdown .dropdown-toggle, > .ods-group > .form-control': {
            borderRadius: '0 !important',
          },
          ...(!stacked
            ? {
                '& > *': {
                  marginLeft: '-1px !important',
                },
                '& > :first-child, & > .dropdown:first-child .dropdown-toggle, > .ods-group:first-child > .form-control, > .ods-input-group:first-child':
                  {
                    marginLeft: '0 !important',
                    ...((round === 'both' || round === 'top') && {
                      borderTopLeftRadius: `${theme.borders.borderRadius()} !important`,
                    }),
                    ...((round === 'both' || round === 'bottom') && {
                      borderBottomLeftRadius: `${theme.borders.borderRadius()} !important`,
                    }),
                  },
                '& > :last-child, & > .dropdown:last-child .dropdown-toggle, > .ods-group:last-child > .form-control, > .ods-input-group:last-child':
                  {
                    ...((round === 'both' || round === 'top') && {
                      borderTopRightRadius: `${theme.borders.borderRadius()} !important`,
                    }),
                    ...((round === 'both' || round === 'bottom') && {
                      borderBottomRightRadius: `${theme.borders.borderRadius()} !important`,
                    }),
                  },
              }
            : {
                '& > *': {
                  marginTop: '-1px !important',
                },
                '> .ods-input-group > .ods-group': {
                  borderRadius: 'inherit !important',

                  '> :first-child': {
                    borderTopLeftRadius: 'inherit !important',
                    borderBottomLeftRadius: 'inherit !important',
                  },
                  '> :last-child': {
                    borderTopRightRadius: 'inherit !important',
                    borderBottomRightRadius: 'inherit !important',
                  },
                },
                '& > :first-child, & > .dropdown:first-child .dropdown-toggle, > .ods-group:first-child > .form-control, > .ods-input-group:first-child':
                  {
                    marginTop: '0 !important',
                    ...((round === 'both' || round === 'top') && {
                      borderTopLeftRadius: `${theme.borders.borderRadius()} !important`,
                      borderTopRightRadius: `${theme.borders.borderRadius()} !important`,
                    }),
                  },
                '& > :last-child, & > .dropdown:last-child .dropdown-toggle, > .ods-group:last-child > .form-control, > .ods-input-group:last-child':
                  {
                    ...((round === 'both' || round === 'bottom') && {
                      borderBottomLeftRadius: `${theme.borders.borderRadius()} !important`,
                      borderBottomRightRadius: `${theme.borders.borderRadius()} !important`,
                    }),
                  },
              }),
        }),
        ...(border && {
          backgroundColor: 'var(--ods-white)',
          border: `1px solid var(--ods-border)`,
          borderRadius: theme.borders.borderRadius(),
          '> .ods-tool-group + .ods-tool-group, > .ods-group + .ods-group': {
            ...(!stacked
              ? {
                  borderLeft: `1px solid var(--ods-border)`,
                }
              : {
                  borderTop: `1px solid var(--ods-border)`,
                }),
          },
          '.ods-tool-group': {
            borderLeft: 'none',
            borderTop: 'none',
          },
        }),
      })}
      {...(name && { name })}
      {...componentProps}
    >
      {delayGroup ? (
        <FloatingDelayGroup delay={{ open: 250, close: 75 }}>{children}</FloatingDelayGroup>
      ) : (
        children
      )}
    </div>
  );
});

export default ODSGroup;
